import { ArrowRight, BookOpen, UserX, BarChart2, Layers, ShieldAlert, ClipboardCheck, ArrowRightLeft } from 'lucide-react'
import { CHALLENGES } from '@/data/modules'
import type { useProgress } from '@/hooks/useProgress'
import { ModuleCard } from '@/components/modules/ModuleCard'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import type { Module, PageView } from '@/types'

interface DashboardProps {
  modules: Module[]
  progress: ReturnType<typeof useProgress>
  darkMode: boolean
  onNavigate: (view: PageView) => void
}

export function Dashboard({ modules, progress, darkMode, onNavigate }: DashboardProps) {
  const firstIncomplete = modules.find(m => !progress.isComplete(m.id))
  const allComplete = progress.completedCount === progress.totalCount

  return (
    <div className="space-y-8 animate-slide-up">
      {/* Logo — sits on the page background above the hero, swaps for dark mode */}
      <div>
        <img
          src={darkMode ? '/images/365hound-long-rev.png' : '/images/365hound-long.png'}
          alt="365 Hound"
          className="h-10 w-auto"
        />
      </div>

      {/* Hero */}
      <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-[var(--accent-blue)] to-[#1e7db8] text-white">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
            <div className="flex-1">
              <h1 className="text-2xl font-bold mb-2">Partner Selling Methods</h1>
              <p className="text-sm opacity-85 max-w-lg leading-relaxed">
                Real-world playbook for generating revenue with 365 Hound. Five progressive methods — from the easiest win to advanced Copilot positioning.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                {!allComplete && firstIncomplete && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="bg-white/20 border-white/30 text-white hover:bg-white/30"
                    onClick={() => onNavigate({ page: 'module', moduleId: firstIncomplete.id })}
                  >
                    {progress.completedCount === 0 ? 'Start learning' : 'Continue'}
                    <ArrowRight size={14} />
                  </Button>
                )}
                {allComplete && (
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <span>🎉 All methods complete!</span>
                  </div>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  className="bg-white/20 border-white/30 text-white hover:bg-white/30"
                  onClick={() => onNavigate({ page: 'quick-reference' })}
                >
                  <BookOpen size={14} />
                  Quick Reference
                </Button>
              </div>
            </div>

            {/* Progress count */}
            <div className="flex-shrink-0 text-right">
              <p className="text-3xl font-bold leading-none">
                {progress.completedCount}<span className="text-white/50">/{progress.totalCount}</span>
              </p>
              <p className="text-xs opacity-70 mt-1">methods complete</p>
            </div>
          </div>
        </div>

        {/* Full-width gradient progress bar */}
        <div className="h-2 w-full bg-white/20">
          <div
            className="h-full transition-all duration-700 ease-out"
            style={{
              width: `${progress.progressPercent}%`,
              background: 'linear-gradient(to right, #EF4C30, #FBB02F, #73B942)',
            }}
          />
        </div>
      </div>

      {/* Why 365 Hound */}
      <section>
        <h2 className="text-base font-semibold text-[var(--text-primary)] mb-3">
          Why 365 Hound exists
        </h2>
        <Card className="text-sm text-[var(--text-secondary)] leading-relaxed">
          <p>
            Azured was doing repetitive manual audits across customer environments. 365 Hound automates those audits and doubles as a customer-facing product — partners use it to drive services revenue, and self-sufficient enterprise customers can use it to optimise their own environment.
          </p>
        </Card>
      </section>

      {/* 6 Core Challenges */}
      <section>
        <h2 className="text-base font-semibold text-[var(--text-primary)] mb-3">
          The 6 problems you'll solve for every customer
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {CHALLENGES.map((challenge, i) => {
            const [title, detail] = challenge.split(' — ')
            const icons = [UserX, BarChart2, Layers, ShieldAlert, ClipboardCheck, ArrowRightLeft]
            const Icon = icons[i]
            return (
              <div
                key={i}
                className="flex items-start gap-3 p-3 rounded-xl bg-[var(--bg-primary)] border border-[var(--border)]"
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[var(--accent-blue-light)] flex items-center justify-center text-[var(--accent-blue)]">
                  <Icon size={13} strokeWidth={2.5} />
                </div>
                <div>
                  <p className="text-sm font-medium text-[var(--text-primary)]">{title}</p>
                  {detail && (
                    <p className="text-xs text-[var(--text-muted)] mt-0.5">{detail}</p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* Module grid */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-[var(--text-primary)]">
            The 5 selling methods
          </h2>
          {progress.completedCount > 0 && (
            <button
              onClick={progress.resetAll}
              className="text-xs text-[var(--text-muted)] hover:text-red-500 transition-colors"
            >
              Reset progress
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {modules.map(mod => (
            <ModuleCard
              key={mod.id}
              module={mod}
              isComplete={progress.isComplete(mod.id)}
              onClick={() => onNavigate({ page: 'module', moduleId: mod.id })}
            />
          ))}
        </div>
      </section>
    </div>
  )
}
